import { useRequest } from "ahooks";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { ChatMetaType, getAccount, getCountry, getSupportChat } from "helpers/backend_helper";
import { User } from "./usersContext";
import { Country } from "pages/Main/Components/AdminPanel/Countries";
import { NotificationContext } from "./notificationContext";
import { GetChatHistoryType } from "helpers/types";

export type MenuKeyType = { key: string; props?: { countryUserId?: string; mainUserId: string } };

export type ContextProps = {
  chats: Chats;
  isAdmin: boolean;
  loading: boolean;
  accountData: User;
  countries: Country[];
  localCountry: Country;
  isAuthorized: boolean;
  getHistoryLoading: boolean;
  chatMetaRef: React.MutableRefObject<ChatMetaType>;
  hasMore: React.MutableRefObject<boolean>;
  sessionHistoryIsUsed: React.MutableRefObject<boolean>;
  refreshCountries: () => void;
  refreshAccountData: (country: string) => void;
  getChatHistory: (props: GetChatHistoryType) => void;
  setChats: React.Dispatch<React.SetStateAction<Chats>>;
  setIsAdmin: React.Dispatch<React.SetStateAction<boolean>>;
  setLocalCountry: React.Dispatch<React.SetStateAction<Country>>;
  setIsAuthorized: React.Dispatch<React.SetStateAction<boolean>>;
};

export type FileRenderType = { content: string; link: string };

export enum ChatType {
  RENT = "RENT",
  PERSONAL = "PERSONAL",
  SUPPORT = "SUPPORT",
}

export type Message = {
  grouping_key?: string | number;
  messages?: any;
};

export type ChatFileMessage = {
  id: number;
  groupId?: string | null;
  createdAt?: string;
  contentType: string;
  content: string;
  // Алиас original для старых клиентов
  link: string;
  original?: string | null;
  preview?: string | null;
  // Размер в МБ
  size?: number | null;
};

export type Chat = { cursor: number; messages: Message[] };

export type Chats = {
  supportChat: Chat;
  debateChat: Chat;
  selected: {
    type: "supportChat" | "debateChat";
    chatId: string;
  };
};

export const AdminContext = createContext({} as ContextProps);

export const initialMessages = { cursor: 0, messages: [] };
export const AdminProvider = ({ children }: { children: JSX.Element }) => {
  const [isAdmin, setIsAdmin] = useState(true);
  const { openNotificationWithIcon } = useContext(NotificationContext);
  const [isAuthorized, setIsAuthorized] = useState(false);

  const { data: countries, refresh: refreshCountries } = useRequest(getCountry, { ready: isAuthorized });
  const [localCountry, setLocalCountry] = useState<Country>(
    localStorage.getItem("country") ? JSON.parse(localStorage.getItem("country")) : { iso: "ES" },
  );
  const [chats, setChats] = useState<Chats>({ supportChat: initialMessages, debateChat: initialMessages } as Chats);
  const hasMore = useRef(true);
  const newChat = useRef(true);
  const prevId = useRef("");
  const sessionHistoryIsUsed = useRef(false);
  const chatMetaRef = useRef<ChatMetaType>(null);
  const { loading: getHistoryLoading, run: getChatHistory } = useRequest(
    (props: GetChatHistoryType) => {
      if (!prevId) {
        newChat.current = true;
        prevId.current = props.chatId;
      } else {
        newChat.current = prevId.current !== props.chatId;
        prevId.current = props.chatId;
      }
      console.log(props);
      return getSupportChat(props);
    },
    {
      manual: true,
      refreshDeps: [isAuthorized],
      onError: (error: any) => {
        if (error?.response.data.message) {
          openNotificationWithIcon("error", error.response.data.message);
        } else {
          openNotificationWithIcon("error", error.message);
        }
      },
      onSuccess: (data, params) => {
        chatMetaRef.current = data.meta;
        data.message.forEach((mess) => {
          const { messages } = mess;
          messages.forEach((inMess) => {
            if (inMess?.authorType === "CLIENT") {
              inMess.author = {
                avatar: (params as any)[2],
              };
            }
          });
        });
        console.log("MESSAGE", data);
        const sliced = data.message.slice();
        if (!newChat.current && !sessionHistoryIsUsed.current && params[0].cursor !== 0) {
          if (sliced.length) {
            if (hasMore.current) {
              setChats((prev) => {
                return {
                  ...prev,
                  supportChat: { cursor: data.cursor, messages: prev.supportChat.messages.concat(sliced) },
                  selected: { chatId: params[0].chatId, type: "supportChat" },
                };
              });
            } else {
              if (sliced.length < 10) {
                hasMore.current = false;
              }
              setChats((prev) => {
                return {
                  ...prev,
                  supportChat: {
                    cursor: data.cursor,
                    messages: sliced,
                  },
                  selected: { chatId: params[0].chatId, type: "supportChat" },
                };
              });
            }
          } else {
            hasMore.current = false;
          }
        } else if (sliced.length) {
          hasMore.current = sliced.length < 10 ? false : true;
          // const isSessionHistory = sessionHistoryIsUsed.current;
          setChats((prev) => {
            return {
              ...prev,
              supportChat: {
                cursor: data.cursor,
                messages: sliced,
              },
              selected: { chatId: params[0].chatId, type: "supportChat" },
            };
          });
        } else {
          if (newChat.current) {
            setChats((prev) => {
              return {
                ...prev,
                supportChat: { cursor: 0, messages: [] },
                selected: { chatId: params[0].chatId, type: "supportChat" },
              };
            });
          }
          hasMore.current = false;
        }
        if (sessionHistoryIsUsed.current) {
          setTimeout(() => {
            document.getElementById(`message-${sliced[sliced.length - 1].messages[0].id}`)?.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          });
          sessionHistoryIsUsed.current = false;
        }
        newChat.current = false;
      },
    },
  );

  const {
    loading,
    data: accountData,
    run: refreshAccountData,
  } = useRequest(getAccount, {
    ready: !!localStorage.getItem("accessToken"),
  });

  useEffect(() => {
    if (accountData?.id) {
      setIsAuthorized(true);
    }
  }, [accountData]);

  useEffect(() => {
    if (countries) {
      const local = localStorage.getItem("country");
      if (!local) {
        localStorage.setItem("country", JSON.stringify(countries[0]));
        setLocalCountry(countries[0]);
      } else {
        const country = countries.find((c) => c?.iso === localCountry?.iso);
        localStorage.setItem("country", JSON.stringify(country));
        setLocalCountry(country);
      }
    }
  }, [countries, localCountry]);

  return (
    <AdminContext.Provider
      value={{
        chats,
        isAdmin,
        hasMore,
        loading,
        countries,
        accountData,
        chatMetaRef,
        localCountry,
        isAuthorized,
        getHistoryLoading,
        sessionHistoryIsUsed,
        setChats,
        setIsAdmin,
        getChatHistory,
        setIsAuthorized,
        setLocalCountry,
        refreshCountries,
        refreshAccountData,
      }}
    >
      {children}
    </AdminContext.Provider>
  );
};
